/**
 * Media Utilities - Helpers for responsive image rendering
 *
 * Builds srcSet and sizes strings from MediaVariant arrays
 * for use with <img> srcSet/sizes attributes.
 */

import type { MediaVariant } from '../types';

/**
 * Builds an HTML srcSet string from media variants.
 * Example output: "url-200.webp 200w, url-640.webp 640w, url-1024.webp 1024w"
 */
export function buildSrcSet(variants: MediaVariant[]): string {
  return variants
    .filter((v) => v.size !== 'original')
    .sort((a, b) => a.width - b.width)
    .map((v) => `${v.url} ${v.width}w`)
    .join(', ');
}

/**
 * Builds a default sizes string for responsive images.
 * Can be customized per use case.
 */
export function buildSizes(
  mode: 'full-width' | 'card' | 'thumbnail' | 'gallery' = 'full-width',
  galleryColumns?: number
): string {
  switch (mode) {
    case 'thumbnail':
      return '200px';
    case 'card':
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
    case 'gallery': {
      const cols = galleryColumns || 3;
      if (cols === 2) return '(max-width: 768px) 100vw, 50vw';
      if (cols === 4) return '(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 25vw';
      return '(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw';
    }
    case 'full-width':
    default:
      return '(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1920px';
  }
}

/**
 * Gets the file extension for a given image format string.
 */
export function getExtForFormat(format: string): string {
  if (format === 'svg+xml') return 'svg';
  if (format === 'jpeg' || format === 'jpg') return 'jpg';
  return format;
}

/**
 * Picks the URL for a specific size variant, falling back to larger sizes or original.
 */
export function pickVariantUrl(
  variants: MediaVariant[],
  preferredSize: 'thumb' | 'small' | 'medium' | 'large' | 'original'
): string {
  // Build a lookup map in a single pass
  const urlBySize = new Map<string, string>();
  for (const v of variants) {
    urlBySize.set(v.size, v.url);
  }

  // Try exact match, then fall back to larger sizes
  const sizeOrder = ['thumb', 'small', 'medium', 'large', 'original'] as const;
  const startIndex = sizeOrder.indexOf(preferredSize);

  for (let i = startIndex; i < sizeOrder.length; i++) {
    const url = urlBySize.get(sizeOrder[i]);
    if (url) return url;
  }

  // Last resort: return any available variant URL
  if (variants.length > 0) return variants[0].url;
  return '';
}
